/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import dayjs from 'dayjs';
import MainPage from '@/MainPage';
import { Form, Input, TimePicker, message, Select, TreeSelect, Spin } from 'antd';
import { isNil, isUndefined } from 'lodash';
import { Actions, DateFormat } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { SingleDatePicker, HSDayPicker } from '@/Components/Common/HSWidget';
import { ButtonTypes } from '@/Components/Common/CustomButton';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { StringFormats } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { combineDateTime } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import { DownOutlined } from '@ant-design/icons';
import {
  formatString,
  getUsername,
  objectSort,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import {
  CloseIcon,
  NewCloseIcon,
  NewUpdateIcon,
  DeleteIcon,
  AttachmentLinkIcon,
  EditIcon,
} from '@/Components/Common/Icons';
import CustomDateRangePicker from '@/Components/Common/CustomDateRangePicker';
import NewCustomButton from '@/Components/Common/NewCustomButton';
import NonRecurringTaskModal from './NonRecurringTaskModal';
import RecurringTaskModal from './RecurringTaskModal';
import TaskModal from './TaskModal';
import ContactSelectList from '@/Components/Common/ContactSelectList';
import { getContacts } from '@/Components/Schedule/Contacts';
import { convertToUserTimezone } from '@/utilities/helpers/DateTimeHelpers';

const currentSiteIconZip = (state) => state.data.siteIconFiles;
const currentSiteIconManifest = (state) => state.data.siteIconManifest;
const REMINDER_TYPES = {
  HOME_VISIT: 'home_visit',
  REMINDER: 'reminder',
};

export const TaskForm = (props) => {
  const {
    onlyContent,
    action,
    hubRecord,
    userInfo,
    onActionClick,
    openSubMenu = false,
    showHeader = false,
    loginUserTimeformat,
    eventRecord,
    closeModalHandler,
    showModal,
    userTimezone,
    contacts,
    fetchingContacts,
    staffList,
    fetchingStaffList,
    selectedUser,
    reFetchList,
    principalRecord = {},
    contactOrStaffAction = false,
  } = props;
  const [form] = Form.useForm();
  const trans = useSelector(translator);
  const taskApi = useSelector((state) => state.apis.taskApi);
  const userApi = useSelector((state) => state.apis.userApi);
  const scheduleApi = useSelector((state) => state.apis.scheduleApi);
  const siteIconZip = useSelector(currentSiteIconZip);
  const siteIconManifest = useSelector(currentSiteIconManifest);
  const [treeData, setTreeData] = useState([]);

  useEffect(() => {
    async function buildTreeData() {
      if (!siteIconZip || !siteIconManifest || typeof siteIconZip.file !== 'function') {
        console.error('Invalid Zip file.');
        return;
      }
      const tree = [];
      for (const mainCat of Object.keys(siteIconManifest)) {
        const subcats = siteIconManifest[mainCat];
        const children = [];
        let firstChildIconUrl = null;
        for (const subcat of Object.keys(subcats)) {
          const fileName = subcats[subcat];
          const file = siteIconZip.file(fileName);
          if (!file) continue;
          try {
            const blob = await file.async('blob');
            const blobToUse = blob.type ? blob : new Blob([blob], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(blobToUse);
            if (!firstChildIconUrl) {
              firstChildIconUrl = url;
            }
            const subcatDisplay = formatString(subcat, StringFormats.titleCase);
            children.push({
              value: subcat,
              key: subcat,
              title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <img
                    src={url}
                    alt={subcatDisplay}
                    style={{
                      width: 57,
                      height: 41,
                    }}
                  />
                  <div className="category-subCategory-dropdown">
                    <span>
                      {trans(String(subcatDisplay).trim().split(' ').join('_').toUpperCase())}
                    </span>
                  </div>
                </div>
              ),
            });
          } catch (error) {
            console.error(`Error loading ${fileName}`, error);
          }
        }
        tree.push({
          value: mainCat,
          key: mainCat,
          selectable: false,
          title: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {firstChildIconUrl && (
                <img
                  src={firstChildIconUrl}
                  alt={mainCat}
                  style={{
                    width: 57,
                    height: 41,
                    marginTop: 10,
                  }}
                />
              )}
              <span style={{ fontWeight: 600 }}>
                {trans(
                  String(formatString(mainCat, StringFormats.titleCase))
                    .trim()
                    .split(' ')
                    .join('_')
                    .toUpperCase()
                )}
              </span>
            </div>
          ),
          children,
        });
      }
      setTreeData(tree);
    }
    buildTreeData();
  }, [siteIconZip, siteIconManifest]);

  const ReminderDuration = [5, 10, 15, 30, 60];

  const FormattedReminderDuration = ReminderDuration.map((duration) => ({
    value: duration,
    label: duration === 60 ? `1 ${trans('HOUR')}` : `${duration} ${trans('MINUTES')}`,
  }));
  // const isEditForm = useMemo(() => action === Actions.editTask, [action]);
  const isReminderAction =
    action === Actions.addReminderEvent || action === Actions.editReminderEvent;

  const EventTypes = {
    zoom: trans('ZOOM_MEETING_L') || 'Zoom meeting',
    schedule: trans('VIDEOCALL_L') || 'Video call',
    reminder: trans('CALENDAR_REMINDER_L') || 'Calendar reminder',
    home_visit: trans('VISIT') || 'Visit',
  };

  // Filter the EventTypes object based on action type
  // For reminder actions: show "reminder" and "home_visit"
  // For call actions: show "zoom" and "schedule"
  const SortedEventTypes = Object.keys(EventTypes)
    .filter((key) => {
      if (isReminderAction) {
        return key === 'reminder' || key === 'home_visit';
      }
      return key === 'zoom' || key === 'schedule';
    })
    .map((key) => ({ key, value: EventTypes[key] }))
    .sort(objectSort('value'));

  const formatDateTime = (dateMomentObj, hours = 0, minutes = 0) => {
    return moment
      .utc()
      .year(dateMomentObj.year())
      .month(dateMomentObj.month())
      .date(dateMomentObj.date())
      .hours(hours)
      .minutes(minutes)
      .seconds(0)
      .milliseconds(0)
      .format();
  };

  const reminderMinutesOptions = [
    { value: -1, label: trans('NOREMINDER') },
    { value: 0, label: trans('ATSTARTTIME') },
    { value: 5, label: `5 ${trans('MINUTESBEFORE')}` },
    { value: 10, label: `10 ${trans('MINUTESBEFORE')}` },
    { value: 15, label: `15 ${trans('MINUTESBEFORE')}` },
    { value: 30, label: `30 ${trans('MINUTESBEFORE')}` },
    { value: 60, label: `1 ${trans('HOURBEFORE')}` },
  ];

  const FormItemLayout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  };
  const LocalTimezone = userTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  // const isEditForm = useMemo(() => action === Actions.editTask, [action]);
  // isReminderAction already declared above

  const isEditForm = useMemo(() => {
    if (
      action === Actions.editTask ||
      action === Actions.editEvent ||
      action === Actions.editReminderEvent
    ) {
      return true;
    }
    return false;
  }, [action]);

  const isCategoryAllowed = useMemo(() => {
    if (action === Actions.editTask || action === Actions.addTask) {
      return true;
    }
    return false;
  }, [action]);

  const isEventCallTypeAllowed = useMemo(() => {
    if (
      action === Actions.addEvent ||
      action === Actions.editEvent ||
      action === Actions.addReminderEvent ||
      action === Actions.editReminderEvent
    ) {
      return true;
    }
    return false;
  }, [action]);

  const getFormText = (action, eventRecord, type) => {
    if (eventRecord?.event_type === 'home_visit') {
      return type === 'header' ? trans('NEWREMINDERDETAILS') : trans('REMINDERNAME');
    }
    const texts = {
      add_event: { header: trans('NEWCALLDETAILS'), placeholder: trans('ADDANAME') },
      edit_event: { header: trans('NEWCALLDETAILS'), placeholder: trans('ADDANAME') },
      reminder_event: { header: trans('NEWREMINDERDETAILS'), placeholder: trans('REMINDERNAME') },
      edit_reminder_event: {
        header: trans('NEWREMINDERDETAILS'),
        placeholder: trans('REMINDERNAME'),
      },
      add_task: { header: trans('NEWTASKINFORMATION'), placeholder: trans('TASKNAME') },
      edit_task: { header: trans('NEWTASKINFORMATION'), placeholder: trans('TASKNAME') },
      default: { header: trans('NEWTASKINFORMATION'), placeholder: trans('TASKNAME') },
    };

    return texts[action] ? texts[action][type] : texts.default[type];
  };
  // Define a function to get headers
  const getModalHeaderOnActions = (action, eventRecord, formData) => {
    switch (action) {
      case 'add_event':
      case 'edit_event':
        return (
          <div className="form-title">
            {eventRecord?.event_type === 'home_visit' ? (
              <span>{isEditForm ? `${trans('EDIT_REMINDER')}` : trans('CREATEREMINDER')}</span>
            ) : (
              <span>{isEditForm ? `${trans('EDIT_CALL')}` : trans('CREATECALL')}</span>
            )}
          </div>
        );
      case 'reminder_event':
      case 'edit_reminder_event':
        return (
          <div className="form-title">
            <span>{isEditForm ? `${trans('EDIT_REMINDER')}` : trans('CREATEREMINDER')}</span>
          </div>
        );
      case 'add_task':
      case 'edit_task':
        return (
          <div className="form-title">
            <span>{isEditForm ? `${trans('EDIT_TASK')}` : trans('CREATETASK')}</span>
          </div>
        );
      default:
        return action;
    }
  };
  const isEventRecurring = useMemo(() => {
    return eventRecord?.repeat && ['daily', 'custom', 'weekly'].includes(eventRecord?.repeat);
  }, [eventRecord]);
  const [checkedDays, setCheckedDays] = useState(isEditForm ? eventRecord?.days_of_week : []);
  const TaskRepeats = useMemo(() => {
    return {
      none: trans('DOESNOTREPEAT'),
      daily: trans('DAILY'),
      weekly: `${trans('WEEKLY')} ${checkedDays?.length ? `(${checkedDays[0].charAt(0).toUpperCase() + checkedDays[0].slice(1).toLowerCase()})` : ''}`,
      custom: trans('CUSTOMIZE'),
    };
  }, [checkedDays]);

  const handleClose = () => {
    closeModalHandler(true);
    if (contactOrStaffAction) {
      onActionClick(null, null, true)(); //callback to contact or staff actions
    }
  };
  const username = useMemo(() => getUsername(hubRecord), [hubRecord]);

  const SortedRepeats = Object.keys(TaskRepeats).map((key) => ({
    value: key,
    label: TaskRepeats[key],
  }));

  const [processing, setProcessing] = useState(false);
  const currentDate = dayjs().tz(userTimezone);
  const [selectedDate, setSelectedDate] = useState(currentDate);

  const [contactsList, setContactsList] = useState([]);
  const [userContactList, setUserContactList] = useState([]);
  const [initialContactsList, setInitialContactsList] = useState([]);

  const sortedContactList = useMemo(
    () => contactsList.sort(objectSort('firstname')),
    [contactsList]
  );
  const sortedUserContactList = useMemo(
    () => userContactList.sort(objectSort('firstname')),
    [userContactList]
  );

  function revertUTCtoUserTimezone(eventRecord, userTimezone) {
    const startDateTime = convertToUserTimezone(
      eventRecord?.event_startdate,
      eventRecord?.event_starttime,
      userTimezone,
      'full'
    );
    const endDate = convertToUserTimezone(
      eventRecord?.event_enddate,
      eventRecord?.event_starttime,
      userTimezone,
      'date'
    );
    const endTime = convertToUserTimezone(
      eventRecord?.event_startdate,
      eventRecord?.event_endtime,
      userTimezone,
      'time'
    );
    return {
      startDate: startDateTime.date,
      startTime: startDateTime.time,
      endDate: endDate,
      endTime: endTime,
    };
  }

  const localEvent = isEditForm ? revertUTCtoUserTimezone(eventRecord, LocalTimezone) : null;
  const getEventTypeKey = (value) => {
    return Object.keys(EventTypes).find((key) => EventTypes[key] === value);
  };
  const [formData, setFormData] = useState({
    reminder: isEditForm && eventRecord?.reminder ? eventRecord?.reminder / 60 : 30,
    category: isEditForm ? eventRecord?.category : `--${trans('CHOOSECATEGORY')}--`,
    repeat: isEditForm ? (eventRecord?.recurring ? eventRecord?.repeat : 'none') : TaskRepeats.none,
    eventType: isEditForm
      ? EventTypes[eventRecord?.event_type]
      : isReminderAction
        ? EventTypes.reminder
        : EventTypes.schedule,
    url: eventRecord?.meeting_url ?? '',
    event_name: isEditForm ? eventRecord?.event_name : '',
    event_description: isEditForm ? eventRecord?.event_description : '',
    starttime: isEditForm && localEvent ? dayjs(localEvent.startTime, 'HH:mm:ss') : null,
    endtime: isEditForm && localEvent ? dayjs(localEvent.endTime, 'HH:mm:ss') : null,
    reminder_minutes:
      isEditForm &&
      !isNil(eventRecord?.reminder_minutes) &&
      !isUndefined(eventRecord?.reminder_minutes)
        ? eventRecord?.reminder_minutes
        : 0,
  });
  const [activeTab, setActiveTab] = useState('schedule');
  const [selectedRangeDates, setSelectedRangeDates] = useState(() => {
    if (isEditForm && localEvent?.startDate) {
      if (localEvent?.endDate) {
        return [
          dayjs(localEvent.startDate, 'YYYY-MM-DD').startOf('day'),
          dayjs(localEvent.endDate, 'YYYY-MM-DD').endOf('day'),
        ];
      } else {
        return [
          dayjs(localEvent.startDate, 'YYYY-MM-DD').startOf('day'),
          dayjs(localEvent.startDate, 'YYYY-MM-DD').endOf('day'),
        ];
      }
    }
    return [];
  });

  useEffect(() => {
    let { invited_contacts, userHubsData, useruuid } = eventRecord || {};
    const canInviteList = [...(contacts ?? []), ...(staffList ?? [])];
    const contactList = !isEditForm
      ? canInviteList.filter((item) => useruuid !== item.useruuid && item.emailVerified === true)
      : canInviteList.filter(
          (item) =>
            !invited_contacts?.includes(item.useruuid) &&
            useruuid !== item.useruuid &&
            item.emailVerified === true
        );
    const uniqueContactList = [...new Map(contactList.map((a) => [a.useruuid, a])).values()];
    setUserContactList(uniqueContactList);
    const list = [];
    if (eventRecord && Object.keys(eventRecord).length > 0) {
      list.push(eventRecord);
    } else {
      list.push(hubRecord);
    }
    const invitedContacts = isEditForm
      ? getContacts(invited_contacts, [
          ...(contacts ?? []),
          ...(userHubsData ?? []),
          ...(userInfo ? [userInfo] : []),
          ...(staffList ?? []),
        ])
      : list;
    setContactsList(invitedContacts);
    setInitialContactsList(invitedContacts);
    if (!fetchingContacts && !fetchingStaffList)
      invitedContacts
        .filter((item) => !('firstname' in item))
        .forEach((item) => {
          fetchData(item.useruuid);
        });
  }, [eventRecord, contacts, staffList, fetchingContacts, fetchingStaffList]);

  const addContact = (contact) => {
    const newList = userContactList.filter((item) => item.email !== contact.email);
    setContactsList((s) => [...s, contact]);
    setUserContactList(newList);
  };

  const removeCard = (contact) => {
    const newList = contactsList.filter((item) => item.email !== contact.email);
    setUserContactList((s) => [...s, contact]);
    setContactsList(newList);
  };
  const [initialFormData] = useState(formData);
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(false);
  const [isCreateButtonDisabled, setIsCreateButtonDisabled] = useState(true);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isSaveChangesModalVisible, setIsSaveChangesModalVisible] = useState(false);

  const handleSaveChanges = () => {
    setIsSaveChangesModalVisible(true);
  };
  const handleSaveChangesModalConfirm = () => {
    setIsSaveChangesModalVisible(false);
    handleClose();
  };
  const handleSaveChangesModalCancel = () => {
    setIsSaveChangesModalVisible(false);
  };

  const handleDeleteClick = () => {
    setIsDeleteModalVisible(true);
  };
  const handleModalConfirm = () => {
    setIsDeleteModalVisible(false);
    deleteTasks();
  };
  const handleModalCancel = () => {
    setIsDeleteModalVisible(false);
  };
  const deleteTasks = () => {
    setProcessing(false);
    const errorCallback = (error) => {
      setProcessing(false);
      // message.error("Error deleting Event!");
      console.error('Error deleting Event:', error);
    };
    const successCallback = (data) => {
      setProcessing(false);
      // message.success("Event deleted successfully!");
      closeModalHandler();
      if (typeof reFetchList === 'function') {
        reFetchList();
        onActionClick(null, null)();
      } else {
        onActionClick(null, null, true)();
      }
    };
    try {
      setProcessing(true);
      if (isEventRecurring) {
        if ([Actions.editEvent, Actions.editReminderEvent].includes(action)) {
          scheduleApi.cancelAllRecurringEvent(
            eventRecord?.useruuid,
            eventRecord?.schedule_eventuuid,
            {},
            apiCallback({
              failCallback: errorCallback,
              errorCallback,
              successCallback,
            })
          );
        } else {
          taskApi.cancelAllRecurringTasks(
            eventRecord?.useruuid,
            eventRecord?.schedule_eventuuid,
            apiCallback({
              failCallback: errorCallback,
              errorCallback,
              successCallback,
            })
          );
        }
      } else {
        if ([Actions.editEvent, Actions.editReminderEvent].includes(action)) {
          scheduleApi.cancelEvent(
            eventRecord?.useruuid,
            eventRecord?.schedule_eventuuid,
            {},
            apiCallback({
              failCallback: errorCallback,
              errorCallback,
              successCallback,
            })
          );
        } else {
          taskApi.cancelTask(
            eventRecord?.useruuid,
            eventRecord?.schedule_eventuuid,
            apiCallback({
              failCallback: errorCallback,
              errorCallback,
              successCallback,
            })
          );
        }
      }
    } catch (error) {
      errorCallback(error);
    }
  };
  const handleTabChange = (key) => {
    setActiveTab(key);
    onActionClick(null, null, key)();
  };
  const get_utc_offset = (timeZone) => {
    return Intl.DateTimeFormat(navigator.language, {
      timeZoneName: 'longOffset',
      timeZone,
    })
      .formatToParts()
      .find((i) => i.type === 'timeZoneName').value;
  };
  const timezonedisplay = LocalTimezone + ` (${get_utc_offset(LocalTimezone)})`;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  const handleDateChange = (dates) => {
    setSelectedRangeDates(dates);
  };
  useEffect(() => {
    if (isEditForm) {
      const eventStartDate = combineDateTime(
        eventRecord?.event_startdate,
        eventRecord?.event_starttime
      );
      setSelectedDate(eventStartDate);
    }
  }, [eventRecord, action]);
  const handleDateSelect = (date) => {
    if (!date || !date.isValid()) {
      console.error('Invalid date passed to handleDateSelect:', date);
      return;
    }
    const normalizedDate = date.startOf('day').toDate();
    setSelectedDate(normalizedDate);
    form.setFieldsValue({ startDate: normalizedDate });
  };
  const disablePreviousDates = (current) => {
    return current && current.isBefore(dayjs().startOf('day'), 'day');
  };
  const renderDatePicker = () => {
    if (formData?.repeat === TaskRepeats.none || formData?.repeat === 'none') {
      return (
        <Form.Item label={trans('DATE*')} name="date">
          <SingleDatePicker
            DateFormat={DateFormat}
            timeZone={userTimezone}
            defaultValue={selectedDate}
            onDateSelect={handleDateSelect}
            value={selectedDate ? selectedDate : null}
            shouldDisablePastDates={true}
          />
        </Form.Item>
      );
    } else {
      return (
        <Form.Item label={trans('DATE*')} name="date" initialValues={{ date: [] }}>
          <CustomDateRangePicker
            onDateChange={handleDateChange}
            selectedDates={selectedRangeDates}
            disabled={isEditForm}
          />
        </Form.Item>
      );
    }
  };

  useEffect(() => {
    const { event_name, starttime, endtime, repeat, category, eventType, url } = formData;
    const normalizedRepeat = repeat === "Doesn't repeat" ? 'none' : repeat || 'none';
    const areRequiredFieldsFilled = !!(
      event_name &&
      starttime &&
      endtime &&
      (!isCategoryAllowed ||
        (category && category.trim() !== '' && category !== '--Choose Category--')) &&
      (!isEventCallTypeAllowed || eventType)
    );
    const isRepeatValid =
      normalizedRepeat === 'weekly'
        ? checkedDays?.length === 1 && selectedRangeDates?.length === 2
        : normalizedRepeat === 'custom'
          ? checkedDays?.length > 0 && selectedRangeDates?.length === 2
          : normalizedRepeat === 'daily'
            ? selectedRangeDates?.length === 2
            : true;
    // For all event types (including Home visit and Reminder), require at least 1 contact
    const isContactSelected = sortedContactList.length > 0;
    const zoomUrlPattern = /https:\/\/[\w-]*\.?zoom.us\/(j|my)\/[\d\w?=-]+/;
    const isZoomUrlValid = eventType === EventTypes.zoom ? zoomUrlPattern.test(url.trim()) : true;

    setIsCreateButtonDisabled(
      !(areRequiredFieldsFilled && isRepeatValid && isContactSelected && isZoomUrlValid)
    );
  }, [
    formData,
    sortedContactList,
    selectedRangeDates,
    checkedDays,
    isCategoryAllowed,
    isEventCallTypeAllowed,
  ]);

  useEffect(() => {
    // validating form changes on making save changes button enabling or disabling
    const isTimeEqual = (a, b) => dayjs(a).isSame(dayjs(b), 'minute');
    const isArrayEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
    const isContactsChanged = !isArrayEqual(
      contactsList.map((c) => c.useruuid).sort(),
      initialContactsList.map((c) => c.useruuid).sort()
    );
    const isDateChanged =
      isEditForm && !dayjs(selectedDate).isSame(dayjs(eventRecord?.event_startdate), 'day');
    const isFormChanged = Object.keys(formData).some((key) => {
      const value = formData[key];
      const initialValue = initialFormData[key];

      if (key === 'starttime' || key === 'endtime') {
        return !isTimeEqual(value, initialValue);
      }
      if (key === 'url') {
        return eventRecord?.event_type === 'zoom' && value !== initialValue;
      }
      if (key === 'category') {
        const normalizedValue = typeof value === 'string' ? value.trim().toLowerCase() : value;
        const normalizedInitial =
          typeof initialValue === 'string' ? initialValue.trim().toLowerCase() : initialValue;
        return normalizedValue !== normalizedInitial;
      }
      if (['event_name', 'event_description'].includes(key)) {
        const normalizedValue = (value ?? '').replace(/\s/g, '');
        const normalizedInitial = (initialValue ?? '').replace(/\s/g, '');
        return normalizedValue !== normalizedInitial;
      }
      if (key === 'reminder') {
        return (value ?? '') !== (initialValue ?? '');
      }
      if (key === 'reminder_minutes') {
        return (value ?? '') !== (initialValue ?? '');
      }
      return false;
    });
    setIsSaveButtonEnabled(isFormChanged || isDateChanged || isContactsChanged);
  }, [formData, initialFormData, selectedDate, contactsList]);

  // cancel / close condidtion check to show discard pop up or navigating to previous page if no chnages are made in edit form
  const handleConditionalClose = () => {
    if (isSaveButtonEnabled && isEditForm) {
      setIsSaveChangesModalVisible(true);
    } else {
      handleClose();
    }
  };

  const handleValueChange = (name, value) => {
    if (name === 'starttime') {
      const newEndTime = value ? value.add(1, 'hour') : null;
      setFormData((prev) => ({ ...prev, starttime: value, endtime: newEndTime }));
      form.setFieldsValue({ endtime: newEndTime });
    } else if (name === 'repeat') {
      setCheckedDays([]);
      setFormData((prev) => ({ ...prev, repeat: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleOnDayChange = useCallback((days) => {
    setCheckedDays((prev) => {
      if (prev.length === days.length && prev.every((val, idx) => val === days[idx])) {
        return prev;
      }
      return days;
    });
  }, []);

  const fetchData = (mUseruuid) => {
    const errorCallback = (error) => {
      // message.error("Error fetching user information!");
      console.error('Error fetching user information:', error);
    };
    const successCallback = (data) => {
      setContactsList((prevState) =>
        prevState.map((item) => {
          if (item.useruuid === data?.user?.useruuid) return data.user;
          else return item;
        })
      );
    };
    try {
      if (!mUseruuid) return;
      userApi.apiClient.callApi(
        '/hacloud/api/v1/user/{useruuid}',
        'GET',
        { useruuid: mUseruuid },
        {},
        { 'Cache-Control': 'no-cache' },
        {},
        null,
        [],
        [],
        ['application/json'],
        Object,
        null,
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const handleSendClick = () => {
    form
      .validateFields()
      .then(() => onFormSubmit())
      .catch((error) => {
        console.error('Form Field Error', error);
        // message.error("Please fill in all required fields")
      });
  };
  const onFormSubmit = () => {
    const errorCallback = (error) => {
      setProcessing(false);
      const msg = `Error ${isEditForm ? 'Updating' : 'Creating'} Event!`;
      // message.error(msg);
      console.error(msg, error);
    };
    const successCallback = (data) => {
      setProcessing(false);
      reFetchList();
      closeModalHandler();
      if (contactOrStaffAction) {
        onActionClick(null, null, true)(); //callback to contact or staff actions
      }
      const msg = `Event ${isEditForm ? 'Updated' : 'Created'} Successfully!`;
      // message.success(msg);
    };
    try {
      setProcessing(true);
      const userUuid = hubRecord.useruuid;
      const formattedDates = selectedRangeDates.map((date) => moment(date.$d).format('YYYY-MM-DD'));
      const eventTypeKey = getEventTypeKey(formData?.eventType);
      const startTime24 = dayjs(formData?.starttime.$d).format('HH:mm');
      const endTime24 = dayjs(formData?.endtime.$d).format('HH:mm');
      const opts = {
        [isEventCallTypeAllowed ? 'createEvent' : 'createTask']: {
          contacts: isEventCallTypeAllowed
            ? contactsList.map((contact) => ({
                useruuid: contact.useruuid,
              }))
            : [{ useruuid: userUuid }],
          ...(isEventCallTypeAllowed && {
            event_type:
              formData?.eventType === EventTypes.zoom
                ? 'zoom'
                : formData?.eventType === EventTypes.reminder
                  ? 'reminder'
                  : formData?.eventType === EventTypes.schedule
                    ? 'schedule'
                    : formData?.eventType === EventTypes.home_visit
                      ? 'home_visit'
                      : '',
          }),
          ...(formData?.eventType === EventTypes.zoom && { zoom: { url: formData?.url } }),
          event_name: formData?.event_name,
          event_description: formData?.event_description,
          ...(isCategoryAllowed && { category: formData?.category }),
          ...(isEventCallTypeAllowed && {
            reminder_minutes: formData?.reminder_minutes,
          }),
          event_startdate: selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : null,
          ...(formData?.repeat &&
            formData?.repeat !== TaskRepeats.none &&
            formData?.repeat !== 'none' && {
              repeat: formData?.repeat,
              days_of_week: formData?.repeat === 'daily' ? [] : checkedDays,
              event_enddate: formattedDates[1],
              event_startdate: formattedDates[0],
            }),
          timezone: timezonedisplay,
          ...(!isEventCallTypeAllowed && {
            reminder: formData?.reminder > 0 ? formData?.reminder * 60 : formData?.reminder,
          }),
          snoozable: formData?.reminder > 0 ? true : false,
          event_starttime: startTime24,
          event_endtime: endTime24,
          ...(isEventCallTypeAllowed && { schedule_eventuuid: eventRecord?.schedule_eventuuid }),
        },
      };
      if (isEditForm) {
        if (action === Actions.editTask) {
          taskApi.updateTask(
            userUuid,
            eventRecord?.schedule_eventuuid,
            opts,
            apiCallback({
              failCallback: errorCallback,
              errorCallback,
              successCallback,
            })
          );
        } else if ([Actions.editEvent, Actions.editReminderEvent].includes(action)) {
          scheduleApi.udpateEvent(
            userUuid,
            eventRecord?.schedule_eventuuid,
            opts,
            apiCallback({
              failCallback: errorCallback,
              errorCallback,
              successCallback,
            })
          );
        }
      } else {
        if (action === Actions.addTask) {
          taskApi.createTask(
            userUuid,
            opts,
            apiCallback({
              failCallback: errorCallback,
              errorCallback,
              successCallback,
            })
          );
        } else if ([Actions.addEvent, Actions.addReminderEvent].includes(action)) {
          scheduleApi.createEvent(
            userUuid,
            opts,
            apiCallback({
              failCallback: errorCallback,
              errorCallback,
              successCallback,
            })
          );
        }
      }
    } catch (error) {
      errorCallback(error);
    }
  };
  const renderDeleteModal = () => {
    if (!isDeleteModalVisible) return null;
    return isEventRecurring ? (
      <RecurringTaskModal
        isModalOpen={isDeleteModalVisible}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
        modalHeader={`${trans('DELETEPERIODIC')} ${eventRecord?.event_type === 'reminder' || eventRecord?.event_type === 'home_visit' ? trans('REMINDER_L') : eventRecord?.event_type === 'tasks' ? trans('TASK_L') : trans('CALL_L')}`}
      />
    ) : (
      <NonRecurringTaskModal
        isModalOpen={isDeleteModalVisible}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
        showDeleteMsg={true}
        modalHeader={`${trans('DELETE')} ${eventRecord?.event_type === 'reminder' || eventRecord?.event_type === 'home_visit' ? trans('REMINDER_L') : eventRecord?.event_type === 'tasks' ? trans('TASK_L') : trans('CALL_L')}`}
        modalType={
          eventRecord?.event_type === 'reminder' || eventRecord?.event_type === 'home_visit'
            ? trans('REMINDER_L')
            : eventRecord?.event_type === 'tasks'
              ? trans('TASK_L')
              : trans('CALL_L')
        }
        actionType="delete"
        className="recurring-event-modal"
      />
    );
  };
  const renderNonRecurringModal = () => {
    if (!isSaveChangesModalVisible) return null;
    return (
      <NonRecurringTaskModal
        isModalOpen={isSaveChangesModalVisible}
        onConfirm={handleSaveChangesModalConfirm}
        onCancel={handleSaveChangesModalCancel}
        showDeleteMsg={false}
        modalHeader={trans('UNSAVEDCHANGES')}
        saveChangesAction={true}
      />
    );
  };
  const render = (
    <div className="taskform-container">
      <Spin size="large" spinning={processing} tip={trans('PROCESSING')}>
        {renderDeleteModal()}
        {renderNonRecurringModal()}
        <Form
          form={form}
          initialValues={formData}
          {...FormItemLayout}
          className="taskform-container"
        >
          <div className="taskform-card">
            <div className="task-title">
              {getModalHeaderOnActions(action, eventRecord, formData)}
              <div>
                <CloseIcon
                  style={{ height: '20px', width: '20px', cursor: 'pointer', fill: '#4E758E' }}
                  onClick={() => handleConditionalClose()}
                />
              </div>
            </div>
            <div className="form-items-container">
              <div className="column-1" style={{ width: '925px' }}>
                {isEditForm && <span className="general-info-text">{formData?.event_name}</span>}
                <span className="general-info-text">
                  {getFormText(action, eventRecord, 'header')}
                </span>
                <div className="column-1-border">
                  {isCategoryAllowed && (
                    <div className="form-group">
                      <Form.Item
                        name="category"
                        label={trans('CATEGORY*')}
                        className="form-group-treeselect"
                      >
                        <TreeSelect
                          className="select-type"
                          style={{ width: '100%' }}
                          value={formData?.category}
                          treeData={treeData}
                          placeholder="Select activity"
                          treeExpandAction="click"
                          onChange={(e) => handleValueChange('category', e)}
                          switcherIcon={({ expanded }) => (
                            <DownOutlined
                              style={{
                                fontSize: '18px',
                                fontWeight: 1000,
                                transition: '0.6s ease',
                                transform: expanded ? 'rotate(180deg)' : 'rotate(90deg)',
                              }}
                            />
                          )}
                        />
                      </Form.Item>
                    </div>
                  )}
                  <div className="form-group" style={{ marginTop: '0px' }}>
                    <Form.Item
                      label={
                        action === Actions.addEvent ||
                        (action === Actions.editEvent &&
                          eventRecord?.event_type !== REMINDER_TYPES.HOME_VISIT)
                          ? trans('NAMETHECALL*')
                          : trans('NAME*')
                      }
                      name="event_name"
                    >
                      <Input
                        placeholder={getFormText(action, eventRecord, 'placeholder')}
                        name="event_name"
                        onChange={handleInputChange}
                        defaultValue={formData?.event_name}
                      />
                    </Form.Item>
                  </div>
                  {isEventCallTypeAllowed && (
                    <Form.Item
                      label={
                        action === Actions.addEvent ||
                        (action === Actions.editEvent &&
                          eventRecord?.event_type !== REMINDER_TYPES.HOME_VISIT)
                          ? trans('CALLTYPE*')
                          : trans('EVENTTYPE*')
                      }
                      name="eventType"
                      className="form-group-select"
                    >
                      <Select
                        disabled={isEditForm}
                        onChange={(e) => handleValueChange('eventType', e)}
                        defaultValue={EventTypes.schedule}
                        value={formData?.eventType}
                        name="eventType"
                        className="select-type"
                      >
                        {SortedEventTypes.map((option) => (
                          <Select.Option key={option.key} value={option.value}>
                            {option.value}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {form.getFieldValue('eventType') === EventTypes.zoom && (
                    <div className="event-zoom-container">
                      <div className="zoom-card">
                        <div className="form-group" style={{ marginTop: '30px' }}>
                          <Form.Item
                            label={''}
                            name="url"
                            rules={[
                              {
                                required: true,
                                message: trans('PLEASEENTERTHEURL'),
                              },
                              {
                                pattern: /https:\/\/[\w-]*\.?zoom.us\/(j|my)\/[\d\w?=-]+/,
                                message: trans('PLEASEENTERAVALIDZOOMMEETINGURL'),
                              },
                            ]}
                          >
                            <Input
                              placeholder={trans('PASTEMEETINGLINKHERE')}
                              className="zoom-url-display"
                              style={{ width: '100%' }}
                              name="url"
                              onChange={(e) => handleInputChange(e)}
                              defaultValue={formData?.url}
                              prefix={Actions.editEvent === 'edit_event' && <AttachmentLinkIcon />} // Add attachment icon here
                              suffix={Actions.editEvent === 'edit_event' && <EditIcon />} // Add edit button here
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="form-row" style={{ marginTop: '25px' }}>
                    <Form.Item
                      name="repeat"
                      className="form-group-select"
                      label={trans('REPEATS*')}
                    >
                      <Select
                        onChange={(e) => handleValueChange('repeat', e)}
                        defaultValue={TaskRepeats.none}
                        value={formData?.repeat}
                        name="repeat"
                        className="select-type"
                        options={SortedRepeats}
                        disabled={isEditForm}
                      />
                    </Form.Item>
                  </div>
                  {formData?.repeat === 'custom' && (
                    <div className="form-row flex-space-between">
                      <div
                        className="form-group flex-row"
                        style={{
                          gap: '16px',
                          alignItems: 'baseline',
                          marginBottom: '-30px',
                        }}
                      >
                        <Form.Item name="days_of_week">
                          <HSDayPicker
                            onDayChange={handleOnDayChange}
                            name="days_of_week"
                            days={checkedDays}
                            allowMultiple={true}
                            disabled={isEditForm}
                            repeatKey={formData?.repeat}
                          />
                        </Form.Item>
                      </div>
                    </div>
                  )}
                  {formData?.repeat === 'weekly' && (
                    <div className="form-row flex-space-between">
                      <div
                        className="form-group flex-row"
                        style={{
                          gap: '16px',
                          alignItems: 'baseline',
                          marginBottom: '-30px',
                        }}
                      >
                        <Form.Item name="days_of_week">
                          <HSDayPicker
                            onDayChange={handleOnDayChange}
                            name="days_of_week"
                            days={checkedDays}
                            allowMultiple={false}
                            disabled={isEditForm}
                          />
                        </Form.Item>
                      </div>
                    </div>
                  )}
                  {renderDatePicker()}
                  <div
                    className="form-group"
                    style={{ display: 'flex', marginTop: '0px', gap: '20px' }}
                  >
                    <Form.Item
                      label={trans('STARTTIME*')}
                      name="starttime"
                      rules={[
                        {
                          type: 'object',
                          message: trans('PLEASESELECTTIME'),
                        },
                      ]}
                    >
                      <TimePicker
                        name="starttime"
                        style={{ width: '423px' }}
                        allowClear={false}
                        required={true}
                        placeholder={trans('SELECTTIME')}
                        format={loginUserTimeformat?.is24hoursformat ? 'HH:mm' : 'h:mm a'}
                        size="large"
                        onChange={(value) => handleValueChange('starttime', value)}
                        minuteStep={15}
                        disabled={isEditForm && isEventRecurring}
                        suffixIcon={
                          <span
                            style={{
                              color:
                                isEditForm && isEventRecurring ? 'rgba(0, 0, 0, 0.25)' : 'black',
                            }}
                          >
                            {timezonedisplay}
                          </span>
                        }
                      />
                    </Form.Item>
                    <Form.Item
                      label={trans('ENDTIME*')}
                      name="endtime"
                      rules={[
                        {
                          type: 'object',
                          message: trans('PLEASESELECTTIME'),
                        },
                      ]}
                    >
                      <TimePicker
                        name="endtime"
                        style={{ width: '423px' }}
                        allowClear={false}
                        required={true}
                        format={loginUserTimeformat?.is24hoursformat ? 'HH:mm' : 'h:mm a'}
                        size="large"
                        onChange={(value) => handleValueChange('endtime', value)}
                        minuteStep={15}
                        disabled={!formData?.starttime || (isEditForm && isEventRecurring)}
                        placeholder={trans('SELECTTIME')}
                        disabledTime={(current) => {
                          if (!formData?.starttime) return {};
                          const start = formData?.starttime;
                          const startHour = start.hour();
                          const startMinute = start.minute();
                          const disabledHours = () => {
                            const hours = [];
                            for (let i = 0; i < startHour; i++) {
                              hours.push(i);
                            }
                            return hours;
                          };
                          const disabledMinutes = (selectedHour) => {
                            if (selectedHour === startHour) {
                              const minutes = [];
                              for (let i = 0; i <= startMinute; i++) {
                                minutes.push(i);
                              }
                              return minutes;
                            }
                            return [];
                          };
                          return {
                            disabledHours,
                            disabledMinutes,
                          };
                        }}
                        suffixIcon={
                          <span
                            style={{
                              color:
                                isEditForm && isEventRecurring ? 'rgba(0, 0, 0, 0.25)' : 'black',
                            }}
                          >
                            {timezonedisplay}
                          </span>
                        }
                      />
                    </Form.Item>
                  </div>
                  {isEventCallTypeAllowed && (
                    <div className="form-row" style={{ marginTop: '25px' }}>
                      <Form.Item
                        name="reminder_minutes"
                        className="form-group-select"
                        label={trans('REMINDER*')}
                      >
                        <Select
                          onChange={(e) => handleValueChange('reminder_minutes', e)}
                          value={formData?.reminder_minutes}
                          name="reminder_minutes"
                          className="select-type"
                          options={reminderMinutesOptions}
                        />
                      </Form.Item>
                    </div>
                  )}
                  {!isEventCallTypeAllowed && (
                    <div className="form-row ">
                      <Form.Item
                        name="reminder"
                        className="form-group-select"
                        label={trans('TIMEBETWEENREMINDERS*')}
                      >
                        <Select
                          onChange={(e) => handleValueChange('reminder', e)}
                          value={formData?.reminder}
                          name="reminder"
                          className="select-type"
                          options={FormattedReminderDuration}
                        />
                      </Form.Item>
                      <div className="reminder-text">
                        {trans('APPLIESIFTHEUSERCHOOSESTOSNOOZEANALERT')}
                      </div>
                    </div>
                  )}
                  <div className="form-group">
                    <Form.Item name="event_description" label={trans('DESCRIPTION')}>
                      <Input.TextArea
                        placeholder={trans('ADDDESCRIPTION')}
                        name="event_description"
                        onChange={handleInputChange}
                        defaultValue={formData?.event_description}
                        className="form-description"
                      />
                    </Form.Item>
                  </div>
                  <div className="manadatory-text">{trans('*THESEFIELDSAREMANDATORY')}</div>
                </div>
              </div>
              {isEventCallTypeAllowed && (
                <div className="column-2">
                  <span className="general-info-text">{`${trans('PARTICIPANTS')}`}</span>
                  <ContactSelectList
                    fetchingContacts={fetchingContacts || fetchingStaffList}
                    selectedListHeader={''}
                    selectionListHeader={trans('ADDCONTACTS')}
                    selectedContactList={sortedContactList}
                    selectionContactList={sortedUserContactList}
                    onAddClick={(item) => addContact(item)}
                    onRemoveClick={(item) => removeCard(item)}
                    showRelationship={false}
                    selectedUser={selectedUser || username}
                    hubRecord={hubRecord}
                    starteventAction={openSubMenu ? false : true}
                    principalRecord={principalRecord}
                    trans={trans}
                    eventType={formData?.eventType}
                    eventRecord={eventRecord}
                  />
                </div>
              )}
            </div>
            <div className="taskform-action-button-container">
              {isEditForm ? (
                <>
                  <Form.Item>
                    <NewCustomButton
                      className={'delete-form'}
                      label={`${trans('DELETE')} ${action === Actions.editReminderEvent || eventRecord?.event_type === 'home_visit' ? trans('REMINDER') : action === Actions.editEvent ? trans('CALL') : trans('TASK')}`}
                      icon={<DeleteIcon className="delete-icon" />}
                      onClick={handleDeleteClick}
                    />
                  </Form.Item>
                  <Form.Item>
                    <NewCustomButton
                      className={'cancel-form-icon'}
                      label={trans('CANCEL')}
                      icon={<NewCloseIcon className="cancel-icon" />}
                      onClick={handleConditionalClose}
                    />
                  </Form.Item>
                  <Form.Item>
                    <NewCustomButton
                      disabled={!isSaveButtonEnabled}
                      type={ButtonTypes.primary}
                      label={trans('SAVECHANGES')}
                      icon={
                        <NewUpdateIcon
                          className={
                            !isSaveButtonEnabled ? 'save-icon-disabled' : 'save-icon-enabled'
                          }
                        />
                      }
                      onClick={handleSendClick}
                    />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Form.Item>
                    <NewCustomButton
                      className={'cancel-form-icon'}
                      label={trans('CANCEL')}
                      icon={<NewCloseIcon className="cancel-icon" />}
                      onClick={handleClose}
                    />
                  </Form.Item>
                  <Form.Item>
                    <NewCustomButton
                      disabled={isCreateButtonDisabled}
                      type={ButtonTypes.primary}
                      label={trans('CREATE')}
                      icon={
                        <NewUpdateIcon
                          className={
                            !isCreateButtonDisabled ? 'update-icon-enabled' : 'update-icon-disabled'
                          }
                        />
                      }
                      onClick={handleSendClick}
                    />
                  </Form.Item>
                </>
              )}
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  );

  return onlyContent ? (
    <TaskModal showModal={showModal} action={action} closeModalHandler={closeModalHandler}>
      {render}
    </TaskModal>
  ) : (
    <MainPage
      hasSider={showHeader ? false : true}
      activeKey={activeTab}
      openSubMenu={openSubMenu}
      onChange={handleTabChange}
      hubUserRole={hubRecord?.rolename}
    >
      {render}
    </MainPage>
  );
};

TaskForm.propTypes = {
  onlyContent: PropTypes.bool,
  action: PropTypes.string,
  hubRecord: PropTypes.object,
  userInfo: PropTypes.object,
  onActionClick: PropTypes.func,
  openSubMenu: PropTypes.bool,
  showHeader: PropTypes.bool,
  loginUserTimeformat: PropTypes.shape({
    is24hoursformat: PropTypes.bool,
  }),
  eventRecord: PropTypes.object,
  closeModalHandler: PropTypes.func,
  showModal: PropTypes.bool,
  userTimezone: PropTypes.string,
  reFetchList: PropTypes.func,
  principalRecord: PropTypes.object,
};

export default React.memo(TaskForm);
